import { createTheme, alpha, type Theme } from '@mui/material/styles';

import { brand, brandDark, status, statusDark, radii } from './tokens';

/**
 * Light + dark MUI themes for the "Version D · Clinical Bold" redesign.
 *
 * The palette maps the 10NEWTONS design tokens (theme/tokens.ts) onto MUI:
 *   primary  — brand teal (chrome, buttons, links)
 *   secondary— lime accent, kept for continuity with the live IMU chart
 *   success / warning / error — the FUNCTIONAL status colours (good / warn /
 *     bad), deliberately outside the brand teal so status never blurs into
 *     chrome.
 *
 * Component overrides carry the rest of the look — rounded cards with a soft
 * shadow + hairline border, calmer chips/buttons — so the redesign lands
 * app-wide (all roles, every page) from the theme rather than page edits.
 */

const sharedTokens = {
  shape: { borderRadius: radii.base },
  typography: {
    fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
    // Headings carry more weight in Version D — the numbers are the story.
    h4: { fontWeight: 800, letterSpacing: '-0.5px' },
    h5: { fontWeight: 780, letterSpacing: '-0.3px' },
    h6: { fontWeight: 750 },
    subtitle2: { fontWeight: 700 },
    button: { fontWeight: 700 },
  },
} as const;

const softShadow =
  '0 1px 2px rgba(38,70,83,0.06), 0 10px 28px rgba(38,70,83,0.10)';
const softShadowDark = '0 1px 2px rgba(0,0,0,0.4), 0 8px 24px rgba(0,0,0,0.35)';

export const lightTheme: Theme = createTheme({
  ...sharedTokens,
  palette: {
    mode: 'light',
    primary: { main: brand.main, dark: brand.deep, light: brand.accent },
    secondary: { main: '#9ec93a' },
    success: { main: status.good },
    warning: { main: status.warn },
    error: { main: status.bad },
    info: { main: status.info },
    background: { default: brand.bg, paper: '#ffffff' },
    // Body text is a neutral slate, NOT the teal chrome ink — keeps reading
    // surfaces off-teal while the nav rail + table headers stay deep teal.
    text: { primary: brand.text, secondary: brand.muted },
    divider: brand.hair,
  },
  components: {
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: radii.card,
          border: `1px solid ${brand.hair}`,
          boxShadow: softShadow,
          backgroundImage: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 700, borderRadius: radii.base } },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', borderRadius: radii.base },
        contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
      },
    },
    // Bold column headers on every table, app-wide, on the ink nav-rail
    // colour with light text — a strong header band distinct from the body.
    // Vertical padding is pinned (and re-pinned for the `size="small"`
    // variant, which would otherwise use ~6px) so every table's header
    // band is the SAME height across every role / view, whatever density
    // the body rows use.
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
          backgroundColor: brand.ink,
          color: '#fff',
          paddingTop: 10,
          paddingBottom: 10,
          lineHeight: 1.5,
          '&.MuiTableCell-sizeSmall': {
            paddingTop: 10,
            paddingBottom: 10,
          },
        },
      },
    },
    // Sort labels only ever sit inside the ink head band, so keep them
    // white in every state — otherwise MUI's active state paints the
    // label + arrow with text.primary (dark), i.e. black on black.
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          color: 'inherit',
          '&:hover': { color: '#fff' },
          '&.Mui-active': { color: '#fff' },
          '&.Mui-focusVisible': { color: '#fff' },
          '&.Mui-active .MuiTableSortLabel-icon': { color: '#fff' },
          '& .MuiTableSortLabel-icon': { color: 'inherit', opacity: 0.5 },
        },
      },
    },
  },
});

export const darkTheme: Theme = createTheme({
  ...sharedTokens,
  palette: {
    mode: 'dark',
    // Slightly desaturated brand colours read better as accents on dark.
    primary: { main: brandDark.main, dark: brandDark.deep, light: brandDark.accent },
    secondary: { main: '#b6dc5b' },
    success: { main: statusDark.good },
    warning: { main: statusDark.warn },
    error: { main: statusDark.bad },
    info: { main: statusDark.info },
    background: { default: brandDark.bg, paper: brandDark.paper },
    divider: brandDark.hair,
  },
  components: {
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: radii.card,
          border: `1px solid ${brandDark.hair}`,
          boxShadow: softShadowDark,
          backgroundImage: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 700, borderRadius: radii.base } },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', borderRadius: radii.base },
        contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
      },
    },
    // Bold column headers on every table, app-wide, on the ink nav-rail
    // colour with light text — a strong header band distinct from the body.
    // See lightTheme: header vertical padding is pinned across densities so
    // every table's header band is the same height in every role / view.
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
          backgroundColor: brandDark.ink,
          color: '#fff',
          paddingTop: 10,
          paddingBottom: 10,
          lineHeight: 1.5,
          '&.MuiTableCell-sizeSmall': {
            paddingTop: 10,
            paddingBottom: 10,
          },
        },
      },
    },
    // See lightTheme: keep sort labels white on the ink head band.
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          color: 'inherit',
          '&:hover': { color: '#fff' },
          '&.Mui-active': { color: '#fff' },
          '&.Mui-focusVisible': { color: '#fff' },
          '&.Mui-active .MuiTableSortLabel-icon': { color: '#fff' },
          '& .MuiTableSortLabel-icon': { color: 'inherit', opacity: 0.5 },
        },
      },
    },
  },
});

/** Ink colour for the nav rail, resolved per mode (Sidebar imports this). */
export function navInk(mode: 'light' | 'dark') {
  return mode === 'dark' ? brandDark.ink : brand.ink;
}

/** Accent used for the selected-nav indicator, per mode. */
export function navAccent(mode: 'light' | 'dark') {
  return mode === 'dark' ? brandDark.accent : brand.accent;
}

/** Convenience: a translucent wash of the nav accent, e.g. selected bg. */
export function navSelectedBg(mode: 'light' | 'dark') {
  return alpha(navAccent(mode), mode === 'dark' ? 0.16 : 0.18);
}
